import { 
  Component,
  Element,  
  Fragment, 
  h,
  Host,
  Listen,
  Prop, 
  State,
  Watch
} from '@stencil/core';

import * as d3 from 'd3';
import {
  DATA_VISUALIZATION_HUE,
  DATA_VISUALIZATION_HUE_VARIANT,
} from '../../constants';
import { ILineChart, LineChart } from '../../models/line-chart-model';
import { getChartModel } from '../../utils/model-helper';
import { CHART_TYPE, LINE_CHART_INVALID_CONFIG } from '../../constants';
import { IChartData } from '../../models';

@Component({
  tag: 'uui-line-chart',
  styleUrl: 'uui-line-chart.scss',
  shadow: true
})
export class UuiLineChart {
  @Element() element: HTMLElement;

  @Prop() config: ILineChart;
  @State() closestChartData: IChartData[] = [];
  @State() width: number;

  public svgElement: any;

  model: LineChart;
  toolTipElement: HTMLElement;
  hasEmptyMessageSlot: boolean;

  @Listen('resize', { target: 'window' })
  windowResize() {
    this.drawChart();
  }

  @Watch('config')
  onConfigChanged(): void {
    this.buildModel();
    this.drawChart();
  }

  componentWillLoad(): void {
    this.hasEmptyMessageSlot = !!this.element.querySelector(
      '[slot="empty-message"]'
    );
    this.buildModel();
  }

  componentDidLoad(): void {
    this.drawChart();
  }

  buildModel(): void {
    // To handle empty or null config
    if (!this.config || Object.keys(this.config).length == 0) {
      this.config = LINE_CHART_INVALID_CONFIG as ILineChart;
    }


    this.model = getChartModel<CHART_TYPE.LINE>(
      this.config as ILineChart
    );
  }

  drawChart() {
    if (!this.model?.series?.length) {
      return;
    }

    this.width = this.model.stretchToMaxWidth
      ? this.element?.getBoundingClientRect().width
      : this.model.width;

    this.model.setPlotAreaWidth(this.width);
   
    this.svgElement = d3
      .select(this.element.shadowRoot.querySelector('svg'))
      .attr('width', this.width)
      .attr('height', this.model.height);

    this.svgElement.selectAll('*').remove();

    const lineChart = this.svgElement
      .append('g')
      .attr(
        'transform',
        `translate(${this.model.plotAreaLeftMargin}, ${this.model.plotAreaTopMargin})`
      );

    // X Axis Title
    if (this.model.xAxis.showAxisLabel) {
      this.svgElement
        .append('text')
        .attr(
          'transform',
          `translate(${(this.width) / 2} ${
            this.model.height
          })`
        )
        .attr('text-anchor', 'middle')
        .text(this.model.xAxis.title)
        .attr('class', 'uui-dv-axis-label');
    }
      
    // Y Axis Title
    if (this.model.yAxis.showAxisLabel) {
      this.svgElement
        .append('text')
        .attr(
          'transform',
          `translate(10, ${
            (this.model.height) / 2
          }) rotate(-90)`
        )
        .attr('text-anchor', 'middle')
        .text(this.model.yAxis.title)
        .attr('class', 'uui-dv-axis-label');
    }

    const xAxisSvg = lineChart
      .append('g')
      .attr('class', 'chart__axis uui-dv-data-label')
      .attr('transform', `translate(0, ${this.model.plotAreaHeight})`)
      .call(this.model.bottomAxis);

    if (!this.model.xAxis.showAxisLine) {
      xAxisSvg.attr('class', 'chart__axis--hidden uui-dv-data-label');
    }

    const yAxisSvg = lineChart
      .append('g')
      .attr('class', 'chart__axis uui-dv-data-label')
      .call(this.model.leftAxis);

    if (!this.model.yAxis.showAxisLine) {
      yAxisSvg.attr('class', 'chart__axis--hidden uui-dv-data-label');
    }

    const line = d3
      .line()
      .x((d: any) => this.model.getLineOriginCoordinateX(this.model.xScale, d))
      .y((d: any) => this.model.getLineOriginCoordinateY(this.model.yScale, d)); 
 
    this.model.series.forEach((series, seriesIndex) => {
      const lines = lineChart
        .append('path')
        .datum(series.data)
        .attr('class', 'line')
        .attr('d', line); 

      if (this.model.tooltip) {
        lines
          .clone()
          .attr('class', 'chart__svg--hoverable-line');
      }

      lines
        .attr('stroke', (d: any) =>
          this.model.getColorForDataNIndex(d, seriesIndex)
        )
        .style('fill', 'none')
        .style('stroke-width', this.model.strokeWidth); 
    });

    if(this.model.target) {
      this.appendTarget(lineChart);
    }

    if(this.model.tooltip) {
      this.appendTooltip(lineChart);
    }  
  }

  getChartTitle(): JSX.Element {
    return (
      (this.model.title || this.model.subTitle) && (
      <uui-chart-title model={this.model}>
        {this.model.title?.useSlot && (
          <slot name="title" slot="title"></slot>
        )}
        {this.model.subTitle?.useSlot && (
          <slot name="sub-title" slot="sub-title"></slot>
        )}
      </uui-chart-title>
      )
    );
  }

  public appendTarget(svgElement: any) {
    var targetColor = this.model.target?.color ??
        DATA_VISUALIZATION_HUE[DATA_VISUALIZATION_HUE_VARIANT.MAGENTA];

    const data = this.model.series[0]?.data;
    const targetLine = svgElement
      .append('line')
      .attr('class', 'chart__target-line')
      .attr('x1', this.model.getLineOriginCoordinateX(this.model.xScale, data[0]))
      .attr('y1', this.model.yScale(this.model.target?.y))
      .attr('x2',  this.model.getLineOriginCoordinateX(this.model.xScale, data[data?.length - 1]))
      .attr('y2', this.model.yScale(this.model.target.y))
      .attr('stroke', targetColor);  
      
    if (this.model.tooltip) {
      targetLine
        .clone()
        .attr('class', 'chart__svg--hoverable-line');
    }
  }

  //#region - Tooltip

  public appendTooltip(svgElement: any) {
    const tooltipGroup = svgElement
        .append('g')
        .attr('class', 'tooltip-marker-group')
        .style('opacity', 0);
  
    tooltipGroup.append('line')
      .attr('class', 'chart__tooltip--marker')
      .attr('x1', 0)
      .attr('y1', 0)
      .attr('x2', 0)
      .attr('y2', this.model.plotAreaHeight);
  
    tooltipGroup.append('circle')
      .attr('class', 'chart__tooltip--marker-dot')
      .attr('cx', 0)
      .attr('cy', 0)
      .attr('r', this.model.tooltipMarkerDotRadius);

    svgElement
      .selectAll('.chart__svg--hoverable-line')
        .on('mouseover', (e: MouseEvent) => {
          this.updateTooltipPosition(e);
          this.toogleTooltip(true);
        })
        .on('mousemove', (e: MouseEvent) => 
          this.updateTooltipPosition(e)
        )
        .on('mouseout', () => 
          this.toogleTooltip(false)
        );
  }

  toogleTooltip(isShow: boolean): void {
    this.svgElement
      .select('.tooltip-marker-group')
      .style('opacity', isShow ? '1' : '0');
    this.toolTipElement.style.opacity = isShow ? '1' : '0';

    if (!isShow) {
      this.toolTipElement.style.top = '-1000px';
      this.toolTipElement.style.left = '-100px';
    }
  }

  updateTooltipPosition(event: MouseEvent): void {
    const mouseX = d3.pointer(event)[0];
    const svgElementX = this.element?.getBoundingClientRect().left;

    // Find the index of the closest range value
    let closestIndex = 0;
    let closestDistance = Infinity;
  
    this.model.xScale.domain().forEach((d, i) => {
      const xPosition = this.model.xScale(d) + this.model.xScale.bandwidth() / 2;
      const distance = Math.abs(mouseX - xPosition);
  
      if (distance < closestDistance) {
        closestIndex = i;
        closestDistance = distance;
      }
    });
  
    // Retrieve the closest data points for each line
    this.closestChartData = this.model.series.map(x => x.data[closestIndex]);

    const xRangeValue = this.model.xScale(this.model.xScale.domain()[closestIndex]) 
        + this.model.xScale.bandwidth() / 2;

    this.svgElement
      .select('.tooltip-marker-group')
      .attr('transform', `translate(${xRangeValue}, 0)`);

    const xPostion = svgElementX + this.model.plotAreaLeftMargin + xRangeValue + 10;
    const documentWidth = document.body.offsetWidth;
    const rightSpace = documentWidth - (svgElementX + xRangeValue);
    const markerLineTooltipGap = 2;

    const tooltipWidth =
      this.toolTipElement?.getBoundingClientRect()?.width + 20;

    if (rightSpace <= tooltipWidth) {
      this.toolTipElement.classList.remove(
        'chart__tooltip--container-pointer-left'
      );
      this.toolTipElement.classList.add(
        'chart__tooltip--container-pointer-right'
      );
     this.toolTipElement.style.left = `${xPostion - markerLineTooltipGap - tooltipWidth}px`;
    } else {
      this.toolTipElement.classList.remove(
        'chart__tooltip--container-pointer-right'
      );
      this.toolTipElement.classList.add(
        'chart__tooltip--container-pointer-left'
      );
      this.toolTipElement.style.left = `${xPostion + markerLineTooltipGap}px`;
    }
    
    this.toolTipElement.style.top = `${event.y - 12}px`;
  }

  getChartTooltip(): JSX.Element {
    const rows = this.getTooltip(this.closestChartData);

      return (
        <Fragment>
          <div class="chart__legend--flex-centre">{rows}</div>
        </Fragment>
      );
  }

  getTooltip(data: IChartData[]): JSX.Element {
    if(!data?.length) {
      return;
    }

    return (
      <div class="chart__legend--flex-column">
        {this.model.tooltip.showTitle && data && (
          <div class="uui-dv-legend-title">{data[0]?.name}</div>
        )}

        <div class="chart__legend--row-container">
          {data.map((d, index) =>
            this.getTooltipRow(d, index)
          )}

          {this.model.target && this.getTooltipRow(this.model.target, 0, true)}
        </div>
      </div>
    );
  }

  getTooltipRow(data: IChartData, index: number, isTargetValue = false): JSX.Element { 
    return (
      <div class="chart__legend--flex-row">
        <div class="chart__tooltip--label">
          {!isTargetValue && 
            <Fragment>
              <div class="chart__legend--dot"
                style={{
                  backgroundColor: this.model.getColorForDataNIndex(data, index)
                }}
              ></div>
              <div class="uui-dv-legend-label">{this.model.series[index]?.name}</div>
            </Fragment>}
          {isTargetValue &&
            <Fragment>
              <svg class="chart__legend-line">
                <line x1="0" x2="12" y1="6" y2="6" class="chart__legend-line--dashed"></line>
              </svg>
              <div class="uui-dv-legend-label">{data.name}</div>
            </Fragment>}       
        </div>
        <div class="chart__tooltip--value uui-dv-legend-label">
          {this.model.valuePrefix}{data.displayLabelY || data.y}{this.model.valueSuffix}
        </div>
      </div>
    );
  }
  //#endregion

  //#region - Tooltip

  getChartLegend(): JSX.Element {
    const legendItems = this.model.computedLengedLabels.map(
      (legendName, legendIndex) => {
        return this.getLegendItem(legendName, legendIndex);
      }
    );

    if(this.model.target) {
      legendItems.push(this.getLegendItem(this.model.target?.name, 0, true));
    }

    return (
      <div class="chart__legend--flex-centre"> 
      {legendItems} 
      </div>);
  }

  getLegendItem(legendName: string, legendIndex: number, isTargetLegend = false): JSX.Element {
    return (
      <Fragment>
        <div class="chart__legend--flex-row">
          {/* -------- Legend Color Palete -------------- */}
          {!isTargetLegend && <div
            class="chart__legend--dot"
            style={{
              backgroundColor: this.model.getColorForDataNIndex(
                this.model.series[0].data[0],
                legendIndex
              )
            }}
          ></div>}

          {isTargetLegend &&
            <svg class="chart__legend-line">
              <line x1="0" x2="12" y1="6" y2="6" class="chart__legend-line--dashed"></line>
            </svg>}

          {/* -------- Legend Label ------------ */}
          <div class="chart__legend-label uui-dv-legend-label">
            {legendName}
          </div>
          </div>
      </Fragment>);
  }

  getLegendItems(legendItems: JSX.Element | JSX.Element[]): JSX.Element {
    return <div class="chart__legend--flex-centre">{legendItems}</div>;
  }
  //#endregion

  render() {
    const chartSectionClass = this.model.isShowChart
      ? 'chart__wrapper'
      : 'chart--hidden';

    const viewBoxCoords =
      this.model.stretchToMaxWidth && this.width
        ? `0 0 ${this.width} ${this.model.height}`
        : '';

    if(!this.model.isShowChart) {
      return (
        <div>
          {this.getChartTitle()}
          <uui-chart-empty-message
            model={this.model}
            hasSlot={this.hasEmptyMessageSlot}
          >
            <slot name="empty-message" slot="empty-message"></slot>
          </uui-chart-empty-message>
        </div>
      );
    }

    return (
      <Host>
        {/*--------------- Title --------- */}
        {this.getChartTitle()}

        <section class={chartSectionClass}>
          {/* --------- Chart ------------- */}
          <svg
            class="chart__svg"
            {...(this.model.stretchToMaxWidth &&
              this.width && { viewBox: viewBoxCoords })}
            preserveAspectRatio="none"
          ></svg>

          {/* --------- Legend ------------- */}
          {this.model.legend && this.model.legend.showLegend && this.getChartLegend()}      
        </section>

        {/* ------------ Tooltip -------------- */}
        {this.model.tooltip && (
          <div
            class="chart__tooltip--container chart__tooltip--container-pointer-left"
            ref={el => (this.toolTipElement = el)}
          >
            {this.closestChartData?.length && this.getChartTooltip()}
          </div>
        )}
      </Host>
    );
  }
}
